import React, { useState, useEffect, useRef } from 'react';
import { User, Utensils, Award, Users } from 'lucide-react';

const data = [
  {
    icon: <User size={30} color="#D4A762" />,
    number: '04', // Target number: 4
    label: 'Professional Chefs',
  },
  {
    icon: <Utensils size={30} color="#D4A762" />,
    number: '450', // Target number: 450
    label: 'Items Of Food',
  },
  {
    icon: <Award size={30} color="#D4A762" />,
    number: '4+', // Target number: 4
    label: 'Years Of Experience',
  },
  {
    icon: <Users size={30} color="#D4A762" />,
    number: '360+', // Target number: 360
    label: 'Satisfied Clients',
  },
];

const ExperienceSection = () => {
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 640);
  const [inView, setInView] = useState(false); // State for section visibility
  const sectionRef = useRef(null); // Ref for observing the section

  // State to hold animated numbers. Initialize to 0.
  // We'll parse the 'number' string to an integer, removing '+' if present.
  const [animatedNumbers, setAnimatedNumbers] = useState(
    data.map(() => 0)
  );

  // Refs to store animation frame IDs to cancel them
  const animationFrameRefs = useRef([]);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Intersection Observer for scroll animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setInView(entry.isIntersecting);
        });
      },
      {
        threshold: 0.2, // Trigger when 20% of the component is visible
      }
    );

    const currentSection = sectionRef.current;

    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []); // Empty dependency array means this runs once on mount


  // Counting animation effect
  useEffect(() => {
    if (inView) {
      // Clear any existing animations before starting new ones
      animationFrameRefs.current.forEach(id => cancelAnimationFrame(id));
      animationFrameRefs.current = [];

      data.forEach((item, index) => {
        const targetNumber = parseInt(item.number.replace('+', ''), 10);
        const duration = 1500; // milliseconds
        let start = null;

        const animate = (timestamp) => {
          if (!start) start = timestamp;
          const progress = timestamp - start;
          const percentage = Math.min(progress / duration, 1);
          const currentNumber = Math.floor(percentage * targetNumber);

          setAnimatedNumbers(prevNumbers => {
            const newNumbers = [...prevNumbers];
            newNumbers[index] = currentNumber;
            return newNumbers;
          });

          if (percentage < 1) {
            animationFrameRefs.current[index] = requestAnimationFrame(animate);
          }
        };

        animationFrameRefs.current[index] = requestAnimationFrame(animate);
      });
    } else {
      // If out of view, cancel animations and reset numbers to 0
      animationFrameRefs.current.forEach(id => cancelAnimationFrame(id));
      animationFrameRefs.current = [];
      setAnimatedNumbers(data.map(() => 0));
    }

    // Cleanup on unmount
    return () => {
      animationFrameRefs.current.forEach(id => cancelAnimationFrame(id));
    };
  }, [inView]); // Re-run this effect when inView state changes

  // Helper for scroll-triggered card animations (slide up, fade in)
  const getScrollAnimatedStyle = (delay) => ({
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0)' : 'translateY(50px)',
    transition: `opacity 0.6s ease-out ${delay}s, transform 0.6s ease-out ${delay}s`,
  });

  const dynamicStyles = {
    section: {
      backgroundColor: '#111111',
      color: '#ffffff',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '40px 0',
      flexWrap: 'wrap',
      gap: isSmallScreen ? '20px' : '0',
      paddingLeft: isSmallScreen ? '20px' : '0',
      paddingRight: isSmallScreen ? '20px' : '0',
      overflow: 'hidden', // Hide elements before they slide in
    },
    card: {
      display: 'flex',
      // Adjusted for small screens: column layout
      flexDirection: isSmallScreen ? 'column' : 'row',
      alignItems: 'center',
      gap: '15px',
      margin: isSmallScreen ? '10px 0' : '20px',
      minWidth: isSmallScreen ? '100%' : '200px',
      flexBasis: isSmallScreen ? '100%' : 'auto',
      boxSizing: 'border-box',
      cursor: 'pointer', // Indicate interactivity
      transition: 'transform 0.3s ease, box-shadow 0.3s ease', // For hover
      // Center content on small screens
      textAlign: isSmallScreen ? 'center' : 'left',
      justifyContent: isSmallScreen ? 'center' : 'flex-start', // Center items horizontally if column
    },
    iconCircle: {
      backgroundColor: '#ffffff',
      borderRadius: '50%',
      padding: '5px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '60px',
      minHeight: '60px',
      transition: 'background-color 0.3s ease, transform 0.3s ease, border 0.3s ease', // Added border transition
      border: '2px solid #ffffff', // Add a default border
    },
    icon: {
      transition: 'color 0.3s ease, transform 0.4s ease', // Added transform transition
    },
    number: {
      fontFamily: 'Playfair Display, serif',
      fontSize: '22px',
      fontWeight: '700',
      transition: 'color 0.3s ease', // For hover
      // Center number on small screens
      textAlign: isSmallScreen ? 'center' : 'left',
    },
    label: {
      fontFamily: 'Open Sans, sans-serif',
      fontSize: '14px',
      marginTop: '5px',
      textAlign: 'left',
      transition: 'color 0.3s ease', // For hover
      // Center label on small screens
      textAlign: isSmallScreen ? 'center' : 'left',
    },
    // Container for number and label when in column mode
    textContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: isSmallScreen ? 'center' : 'flex-start', // Center text vertically
      justifyContent: isSmallScreen ? 'center' : 'flex-start',
    }
  };

  return (
    <>
      <style>
        {`
          /* Base styles for the Lucide icons within the circle */
          .experience-icon-svg {
            color: #D4A762; /* Default color */
            transition: color 0.3s ease, transform 0.4s ease; /* Smooth transition for icon color and transform */
          }

          /* Card Hover Effect */
          .experience-card:hover {
            transform: translateY(-8px); /* Lift effect */
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3); /* Subtle shadow */
          }

          /* Icon Circle Hover Effect */
          .experience-card:hover .experience-icon-circle {
            background-color: transparent; /* Transparent background on hover */
            border-color: #D4A762; /* Gold border on hover */
            transform: scale(0.95); /* Slightly shrink the circle */
          }

          /* Icon inside Circle Hover Effect (Rotation and Color Change) */
          .experience-card:hover .experience-icon-svg {
            color: #D4A762; /* Gold color for icon on hover */
            transform: scaleX(1) rotate(360deg); /* Full rotation, no horizontal flip */
          }

          /* Number and Label Text Hover Effect */
          .experience-card:hover .experience-number {
            color: #D4A762; /* Gold color for number on hover */
          }

          .experience-card:hover .experience-label {
            color: #D4A762; /* Gold color for label on hover */
          }

          /* Media queries for responsiveness (as per your original code) */
          @media (max-width: 639px) { /* Changed from 640px to 639px for consistent mobile styling */
            .experience-section-container {
              width: 100%;
              padding: 40px 20px;
              gap: 20px;
              box-sizing: border-box;
            }
            .experience-card {
              min-width: 100%;
              flex-basis: 100%;
              margin: 10px 0;
              flex-direction: column; /* Stack icon, number, label vertically */
              align-items: center; /* Center items horizontally within the card */
              text-align: center; /* Center text within the card */
            }
            .experience-card .experience-number,
            .experience-card .experience-label {
                text-align: center; /* Ensure number and label are centered */
            }
          }
          @media (min-width: 640px) {
            .experience-section-container {
              gap: 0;
              padding: 40px 0;
              width: auto;
              margin: 0;
            }
            .experience-card {
              min-width: 200px;
              flex-basis: auto;
              margin: 20px;
              flex-direction: row; /* Default row layout on larger screens */
              align-items: center; /* Align items to center of row */
              text-align: left; /* Default text alignment for larger screens */
            }
            .experience-card .experience-number,
            .experience-card .experience-label {
                text-align: left; /* Ensure number and label are left-aligned */
            }
          }
        `}
      </style>

      {/* Attach sectionRef to the main div */}
      <div style={dynamicStyles.section} className="experience-section-container" ref={sectionRef}>
        {data.map((item, idx) => (
          <div
            key={idx}
            style={{
              ...dynamicStyles.card,
              ...getScrollAnimatedStyle(idx * 0.1), // Staggered animation
            }}
            className="experience-card"
          >
            <div style={dynamicStyles.iconCircle} className="experience-icon-circle">
              {/* Clone the icon and add a className for targeting in CSS */}
              {React.cloneElement(item.icon, { className: 'experience-icon-svg', style: dynamicStyles.icon })}
            </div>
            <div style={dynamicStyles.textContainer}> {/* Added a container for text */}
              <div style={dynamicStyles.number} className="experience-number">
                {animatedNumbers[idx]}
                {item.number.includes('+') ? '+' : ''} {/* Add '+' back if it was in the original string */}
              </div>
              <div style={dynamicStyles.label} className="experience-label">{item.label}</div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default ExperienceSection;